import { useNavigate } from "react-router-dom";
import {
  Navigation,
  CalendarCheck2,
  FileText,
  ClipboardList,
  Wrench,
  Phone,
  Mail,
  MapPin,
  Building2,
  Route,
  Check,
  Plus,
  ExternalLink,
} from "lucide-react";
import {
  ResponsiveDialog as Dialog,
  ResponsiveDialogContent as DialogContent,
  ResponsiveDialogDescription as DialogDescription,
  ResponsiveDialogFooter as DialogFooter,
  ResponsiveDialogHeader as DialogHeader,
  ResponsiveDialogTitle as DialogTitle,
  ResponsiveDialogBody as DialogBody,
} from "@/components/ui/responsive-dialog";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/StatusPill";
import { KindIcon } from "@/components/Badges";
import { reportUrlFor } from "@/components/calendar/EventActions";
import { KIND_LABEL } from "@/lib/calendar";
import { formatDate, daysText } from "@/lib/format";
import { navLinks, isAppleDevice, haversineKm, formatKm, LOCATION_SOURCE_LABEL } from "@/lib/geo";
import { cn } from "@/lib/utils";

/** Bygg eit kalender-event-objekt (same form som /api/dashboard/calendar) frå kartrad + frist. */
export const eventFromMachine = (m, d) => ({
  id: `${m.id}:${d.kind}`,
  machine_id: m.id,
  kind: d.kind,
  label: m.label,
  customer_id: m.customer_id,
  customer_name: m.customer_name,
  customer_phone: m.customer_phone,
  customer_email: m.customer_email,
  date: d.date,
  status: d.status,
  days_remaining: d.days_remaining,
  planned_date: d.planned_date,
  planned_note: d.planned_note,
});

const DeadlineRow = ({ m, d, onPlan }) => (
  <div className="flex items-center gap-3 rounded-xl border bg-card p-2.5" data-testid={`sheet-deadline-${d.kind}`}>
    <KindIcon kind={d.kind} />
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className="text-sm font-semibold">{KIND_LABEL[d.kind]}</span>
        <StatusPill status={d.status} />
      </div>
      <div className="text-xs text-muted-foreground">
        Frist {formatDate(d.date)} · {daysText(d.days_remaining)}
        {d.planned_date && (
          <span className="ml-1 inline-flex items-center gap-1 rounded-full border px-1.5 py-px text-[11px] font-semibold status-planned">
            <CalendarCheck2 className="h-3 w-3" /> Planlagt {formatDate(d.planned_date)}
          </span>
        )}
      </div>
    </div>
    <Button size="sm" variant="outline" className="h-9 shrink-0" onClick={() => onPlan?.(eventFromMachine(m, d))} data-testid={`sheet-plan-${d.kind}`}>
      <CalendarCheck2 /> {d.planned_date ? "Endre" : "Planlegg"}
    </Button>
  </div>
);

/**
 * MachineSheet – detaljar for ein pin: fristar, plassering, kontakt og handlingar.
 * Mobil: bottom drawer (snap). Desktop: dialog.
 */
export const MachineSheet = ({ machine: m, open, onOpenChange, userPos, tripMode, selected, onToggleSelect, onPlan }) => {
  const navigate = useNavigate();
  if (!m) return null;
  const pos = m.position;
  const links = pos ? navLinks(pos, m.customer_name) : null;
  const apple = isAppleDevice();
  const dist = userPos && pos ? haversineKm(userPos, pos) : null;
  const phone = m.customer_phone ? String(m.customer_phone).replace(/\s+/g, "") : null;
  const posLabel = pos?.source === "machine"
    ? `Overstyrt plassering · ${LOCATION_SOURCE_LABEL[pos.location_source] || "Manuelt"}`
    : `Kundens adresse${pos?.approximate ? " · omtrentleg (postnummer)" : ""}`;

  const go = (to) => {
    onOpenChange(false);
    setTimeout(() => navigate(to), 60);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="machine-sheet">
        <DialogHeader className="min-w-0">
          <div className="flex min-w-0 items-start gap-3 [&>*]:min-w-0">
            <div className="min-w-0 flex-1">
              <DialogTitle className="truncate font-heading text-lg" data-testid="machine-sheet-title">{m.label}</DialogTitle>
              <DialogDescription className="truncate">
                {m.customer_name || "—"}{m.category ? ` · ${m.category}` : ""}{m.serial_number ? ` · SN ${m.serial_number}` : ""}
              </DialogDescription>
            </div>
            <StatusPill status={m.overall_status} className="mt-0.5 shrink-0 md:mr-7" testid="machine-sheet-status" />
          </div>
        </DialogHeader>

        <DialogBody>
          {m.all_deadlines?.length ? (
            <div className="space-y-2">
              {m.all_deadlines.map((d) => <DeadlineRow key={d.kind} m={m} d={d} onPlan={onPlan} />)}
            </div>
          ) : (
            <p className="rounded-xl border border-dashed p-3 text-sm text-muted-foreground">Ingen fristar registrerte på maskina.</p>
          )}

          <div className="flex items-start gap-3 rounded-xl bg-muted/50 p-3" data-testid="machine-sheet-position">
            {pos?.source === "machine" ? <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> : <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />}
            <div className="min-w-0 flex-1 text-sm">
              <div className="font-medium">{pos?.address || "Ukjend adresse"}</div>
              <div className="text-xs text-muted-foreground">
                {posLabel}
                {pos?.note ? ` · ${pos.note}` : ""}
                {dist != null && <span className="ml-1 font-semibold text-foreground">· {formatKm(dist)} unna</span>}
              </div>
            </div>
          </div>

          {(m.contact_person || phone || m.customer_email) && (
            <div className="flex flex-wrap gap-2 text-sm" data-testid="machine-sheet-contact">
              {m.contact_person && <span className="inline-flex h-9 items-center rounded-full bg-muted px-3 text-xs font-medium">{m.contact_person}</span>}
              {phone && (
                <a href={`tel:${phone}`} className="pressable inline-flex h-9 items-center gap-1.5 rounded-full border bg-card px-3 text-xs font-semibold hover:bg-accent" data-testid="machine-sheet-call">
                  <Phone className="h-3.5 w-3.5" /> {m.customer_phone}
                </a>
              )}
              {m.customer_email && (
                <a href={`mailto:${m.customer_email}`} className="pressable inline-flex h-9 items-center gap-1.5 rounded-full border bg-card px-3 text-xs font-semibold hover:bg-accent" data-testid="machine-sheet-email">
                  <Mail className="h-3.5 w-3.5" /> E-post
                </a>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" className="h-11 justify-start" onClick={() => go(reportUrlFor({ machine_id: m.id, kind: m.next?.kind }))} data-testid="machine-sheet-new-report">
              <FileText className="text-primary" /> Servicerapport
            </Button>
            <Button variant="outline" className="h-11 justify-start" onClick={() => go(`/rapportar/ny-jobb?machine=${m.id}`)} data-testid="machine-sheet-new-jobb">
              <ClipboardList /> Jobbrapport
            </Button>
            <Button variant="outline" className="h-11 justify-start" onClick={() => go(`/maskiner/${m.id}`)} data-testid="machine-sheet-open-machine">
              <Wrench /> Opne maskin
            </Button>
            {links && (
              <Button asChild variant="outline" className="h-11 justify-start">
                <a href={apple ? links.google : links.apple} target="_blank" rel="noreferrer" data-testid="machine-sheet-nav-alt">
                  <ExternalLink /> {apple ? "Google Maps" : "Apple Maps"}
                </a>
              </Button>
            )}
          </div>
        </DialogBody>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant={selected ? "secondary" : "outline"}
            className={cn("h-11", selected && "border-primary/40 bg-primary/10 text-foreground hover:bg-primary/15")}
            onClick={() => onToggleSelect?.(m)}
            data-testid="machine-sheet-toggle-trip"
          >
            {selected ? <Check className="text-primary" /> : tripMode ? <Plus /> : <Route />}
            {selected ? "I turen" : "Legg i tur"}
          </Button>
          {links && (
            <Button asChild className="h-11">
              <a href={apple ? links.apple : links.google} target="_blank" rel="noreferrer" data-testid="machine-sheet-navigate">
                <Navigation /> Naviger
              </a>
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
